import { concat } from '@ember/helper';
import { dropTask } from 'ember-concurrency';
import { and, or } from 'tio-ui/utilities';
import { htmlSafe } from '@ember/template';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import ConfirmationDialog from 'tio-ui/components/confirmation-dialog';
import Textarea from 'tio-ui/components/textarea';
import ContributionsAccountingPayments from 'tio-employee/components/contributions/accounting-payments';
import jsonPrettyPrint from 'tio-common/helpers/json-pretty-print';
import PlanSummary from 'tio-employee/components/plan/summary';
import ProfileNotificationHistories from 'tio-common/components/profile/notification-histories';
import ProfilePslf from 'tio-common/components/profile/pslf';
import ProfileRecommendations from 'tio-common/components/profile/recommendations';
import ProfileRoleDelegation from 'tio-common/components/profile/role-delegation/delegation';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import UpdateEmployeeOverrideTable from 'tio-common/components/profile/update-employee-override-table';
import TioPageBreadcrumbs from 'tio-common/components/tio/page/breadcrumbs';
import TioQuicklinkButton from 'tio-common/components/tio/quicklink-button';
import TioQuicklinks from 'tio-common/components/tio/quicklinks';
import TuitionAssistanceProfileTuitionAssistance from 'tio-employee/components/tuition-assistance/profile/tuition-assistance';
import type { Contribution as ContributionModel } from 'tio-data/data/resources/contribution.type';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router';
// This is for types. Anything we have custom may show an error.
import { reloadRecord } from 'tio-data/requests/save-record';
import type { Store as StoreService } from '@warp-drive/core';
import {
  reinstateEmployeeRequest,
  terminateEmployeeRequest,
} from 'tio-data/requests/employee-actions';
import { Header, HStack, Section, VStack } from 'tio-ui/components/layout';
import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import Chip from 'tio-ui/components/chip';

interface UiDetailsBoxSignature {
  Args: {
    details: string;
    detailsLabel: string;
    goToPlans?: () => void;
  };
  Element: HTMLDivElement;
}

interface ErrorDetail {
  detail?: string;
}

interface ErrorResponse {
  errors?: ErrorDetail[];
}

const UiDetailsBox: TOC<UiDetailsBoxSignature> = <template>
  <div class="rounded-md text-center shadow-sm" ...attributes>
    <div class="py-6">
      <span class="tio-text-xl-semibold">{{@details}}</span>
      <br />
      {{#if @goToPlans}}
        <button
          class="text-lg underline outline-none tio-fg-info cursor-pointer"
          type="button"
          {{on "click" @goToPlans}}
        >
          {{t "partner.employee.plan_details"}}
        </button>
      {{else}}
        <span class="text-lg">{{@detailsLabel}}</span>
      {{/if}}
    </div>
  </div>
</template>;

export interface S {
  Args: {
    model: EmployeeModel;
    plan: ContributionModel;
  };
}

export default class EmployeeShowRoute extends Component<S> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  @tracked showEmployeeMenu = false;
  @tracked showActivationModal = false;
  @tracked changelog = '';
  @tracked showPlans = false;
  @tracked submitError?: unknown;
  @tracked hasSubmitted: boolean = false;

  goToPlans = () => {
    this.router.transitionTo('authenticated.admin.plans', this.args.model.company.id);
  };

  toggleShowPlans = () => {
    this.showPlans = !this.showPlans;
  };

  toggleActivationModal = () => {
    this.showEmployeeMenu = false;
    this.showActivationModal = !this.showActivationModal;
  };

  toggleEmployeeMenu = () => {
    this.showEmployeeMenu = !this.showEmployeeMenu;
  };

  get serverErrorMessages() {
    const genericErrorMessage = this.intl.t('error_occurred');
    if (
      this.submitError &&
      typeof this.submitError === 'object' &&
      'errors' in this.submitError &&
      Array.isArray((this.submitError as ErrorResponse).errors)
    ) {
      const errors = (this.submitError as ErrorResponse).errors;
      if (errors) {
        return errors.map((err) => {
          return err.detail || genericErrorMessage;
        });
      }
    }
    if (this.submitError) {
      return [genericErrorMessage];
    }
    return [];
  }

  deactivateOrReactivateEmployee = dropTask(async () => {
    const employee = this.args.model;
    this.submitError = null;
    this.hasSubmitted = true;
    try {
      if (
        employee.participationStatus === 'participating' ||
        employee.participationStatus === 'pending'
      ) {
        // WarpDrive builder write (wave 1.5) — replaces the custom employee
        // adapter `terminateEmployee` method. PLURAL DELETE
        // /employees/{id}; the legacy adapter reloaded the model, so reload here.
        await this.store.request(terminateEmployeeRequest(employee.id!, this.changelog));
        // DELETE /employees/:id is the JSONAPI default destroy → `204 No Content`
        // (renders NO resource), so the builder does NOT refresh the cache — keep
        // the reload via the helper.
        await reloadRecord(this.store, employee);
      } else if (employee.participationStatus === 'deactivated') {
        // WarpDrive builder write (wave 1.5) — replaces the custom employee
        // adapter `reinstateEmployee` method. PLURAL PATCH
        // /employees/{id}/reinstate; reload mirrors the legacy adapter.
        await this.store.request(reinstateEmployeeRequest(employee.id!, this.changelog));
        // /reinstate goes through perform_action → `head 204` (renders NO
        // resource), so the builder does NOT refresh the cache — keep the reload
        // via the helper.
        await reloadRecord(this.store, employee);
      }
      this.showActivationModal = false;
      this.changelog = '';
    } catch (err) {
      this.submitError = err;
      console.error(err);
    }
  });

  get payrollId() {
    return (
      (this.args.model.employeeAddition?.processed?.merged?.['payrollId'] as string) ||
      this.args.model.payrollId ||
      '-'
    );
  }

  get fullName() {
    const firstName =
      this.args.model.employeeAddition?.processed?.merged?.['firstName'] ||
      this.args.model.firstName;

    const lastName =
      this.args.model.employeeAddition?.processed?.merged?.['lastName'] || this.args.model.lastName;

    return `${firstName} ${lastName}`;
  }

  <template>
    <VStack>
      <TioPageBreadcrumbs as |b|>
        <b.crumb
          class="relative! inline-block!"
          @route="authenticated.admin.employees.index"
          @label="Employees"
        />
        <b.crumb
          class="relative! inline-block!"
          @route="authenticated.admin.employees.show"
          @label={{@model.fullName}}
        />
      </TioPageBreadcrumbs>
      <Header>
        {{t "partner.employee.employee_details"}}
        <TioQuicklinks
          @direction="right"
          @show={{this.showEmployeeMenu}}
          @toggle={{this.toggleEmployeeMenu}}
        >
          <TioQuicklinkButton
            @isDisabled={{if
              (or
                (eq @model.participationStatus "pending")
                (eq @model.participationStatus "participating")
              )
              true
              false
            }}
            @buttonText={{t "partner.employee.reactivate"}}
            @method={{this.toggleActivationModal}}
          />
          <br />
          <TioQuicklinkButton
            @isDisabled={{if
              (or
                (eq @model.participationStatus "pending")
                (eq @model.participationStatus "participating")
              )
              false
              true
            }}
            @buttonText={{t "partner.employee.deactivate"}}
            @method={{this.toggleActivationModal}}
          />
          <br />
        </TioQuicklinks>
      </Header>
      <HStack>
        <UiDetailsBox
          @details={{String @model.statistics.user.total_accounts}}
          @detailsLabel={{t "partner.employee.total_accounts"}}
          class="p-4 tio-bg-surface"
        />
        <UiDetailsBox
          @details={{String @model.statistics.user.total_loans}}
          @detailsLabel={{t "partner.employee.total_loans"}}
          class="p-4 tio-bg-surface"
        />
        <UiDetailsBox
          @details={{@model.participationStatus}}
          @detailsLabel={{t "partner.employee.status"}}
          class="p-4 tio-bg-surface"
        />
        <UiDetailsBox
          @details={{concat @model.plan.companyKey " - " @model.plan.name}}
          @detailsLabel={{t "partner.employee.plan_details"}}
          @goToPlans={{this.goToPlans}}
          class="p-4 tio-bg-surface"
        />
      </HStack>

      <Section>
        <:header>{{t "partner.employee.employee_profile"}}</:header>
        <:body>
          <VStack @collapsed={{true}}>
            <VStack>
              <div class="tio-flex-tight">
                <p class="capitalize font-semibold">{{this.fullName}}</p>
                <div>
                  <Chip @size="sm" @intent={{if @model.isActive "success" "danger"}}>
                    {{if @model.isActive (t "common.active") (t "common.deactivated")}}
                  </Chip>
                  {{#if @model.isActive}}
                    <span class="tio-fg-muted ml-1">(
                      {{safeFormatDate
                        @model.createdAt
                        month="long"
                        day="2-digit"
                        year="numeric"
                      }})</span><br />
                  {{else if @model.deactivatedOn}}
                    <span class="tio-fg-muted ml-1">
                      ({{safeFormatDate
                        @model.deactivatedOn
                        month="long"
                        day="2-digit"
                        year="numeric"
                      }})</span><br />
                  {{else}}
                    <span class="tio-fg-muted ml-1">
                      ({{safeFormatDate
                        @model.updatedAt
                        month="long"
                        day="2-digit"
                        year="numeric"
                      }})</span><br />
                  {{/if}}
                </div>
              </div>
              <div class="flex gap-12">
                <Section>
                  <label class="flex capitalize">{{t "common.employees.fields.employee_id"}}</label>
                  <span>{{@model.id}}</span>
                </Section>
                <Section>
                  <label class="flex capitalize">{{t "common.employees.fields.payroll_id"}}</label>
                  <span>{{this.payrollId}}</span>
                </Section>
              </div>
              <UpdateEmployeeOverrideTable @employee={{@model}} />
            </VStack>
          </VStack>
        </:body>
      </Section>
      <Section>
        <:header>{{t "partner.employee.custom_partner_data"}}</:header>
        <:body>
          <VStack>
            {{#each-in @model.employeeAddition.allAttributes as |key value|}}
              <Section @collapsed={{true}}>
                <:header>{{key}}</:header>
                <:body>{{htmlSafe (jsonPrettyPrint value)}}</:body>
              </Section>
            {{/each-in}}
          </VStack>
        </:body>
      </Section>
      <Section @collapsed={{true}}>
        <:header>{{t "partner.employee.payment_transactions"}}</:header>
        <:body>
          <ContributionsAccountingPayments @employee={{@model}} as |payments|>
            {{! @glint-expect-error: not sure}}
            <payments.table class="w-full" />
            {{! @glint-expect-error: not sure}}
            {{#if payments.paymentsData.length}}
              <div class="w-full text-center text-sm p-6 tio-fg-secondary">
                {{! @glint-expect-error: not sure}}
                {{payments.paymentsData.length}}
                {{t "of"}}
                {{! @glint-expect-error: not sure}}
                {{payments.paymentsData.length}}
                {{t "contributions.payments"}}
              </div>
            {{/if}}
          </ContributionsAccountingPayments>
        </:body>
      </Section>

      <PlanSummary
        {{! @glint-expect-error: this could be a bug. models are getting mixed }}
        @changelog={{@changelog}}
        @employee={{@model}}
        @plan={{@plan}}
        {{! @glint-expect-error: this could be a bug. models are getting mixed }}
        @model={{@model}}
        id="plan"
      />

      <ProfileRecommendations @user={{@model.user}} @recommendationType="STRATEGY_FINDER" />
      <ProfilePslf id="pslf_forms" @employee={{@model}} @isPartner={{true}} />
      <ProfileRoleDelegation
        @employee={{@model}}
        @employeeRoute="authenticated.admin.employees.show"
      />

      <TuitionAssistanceProfileTuitionAssistance id="tuition-assistance" @employee={{@model}} />

      <ProfileNotificationHistories id="emails" @employee={{@model}} @user={{@model.user}} />
    </VStack>
    <ConfirmationDialog
      @isOpen={{this.showActivationModal}}
      @title={{if
        (or
          (eq @model.participationStatus "pending") (eq @model.participationStatus "participating")
        )
        (t "partner.employee.deactivate")
        (t "partner.employee.reactivate")
      }}
      @intent="warning"
      @onConfirm={{this.deactivateOrReactivateEmployee.perform}}
      @onCancel={{this.toggleActivationModal}}
      @confirmText={{if
        (or
          (eq @model.participationStatus "pending") (eq @model.participationStatus "participating")
        )
        (t "partner.employee.deactivate")
        (t "partner.employee.reactivate")
      }}
      @isRunning={{this.deactivateOrReactivateEmployee.isRunning}}
    >
      <p class="mb-2">
        {{if
          (or
            (eq @model.participationStatus "pending")
            (eq @model.participationStatus "participating")
          )
          (t "partner.employee.deactivate_text")
          (t "partner.employee.reactivate_text")
        }}
      </p>
      <Textarea
        @label={{t "common.reason"}}
        @value={{this.changelog}}
        @onChange={{fn (mut this.changelog)}}
        class="mb-4 w-full"
      />
      {{#if (and this.hasSubmitted this.serverErrorMessages.length)}}
        <ul class="tio-fg-danger my-2">
          {{#each this.serverErrorMessages as |message|}}
            <li class="whitespace-normal">{{message}}</li>
          {{/each}}
        </ul>
      {{/if}}
    </ConfirmationDialog>
  </template>
}
